/**
 * The feature catalog's helpers (@aiga/app-config/permissions): the default-tier rule, the
 * `perm_name` conversions and the Feature Access matrix groupings. Kept beside
 * ./feature-catalog.ts (its 800-line budget); ../permissions.ts exports both.
 */
import { ADMIN_ROLES, BROKER_ROLE, BROKERAGE_ASSISTANT_ROLE, MANAGER_PLUS_ROLES, RESEARCH_TIER_ROLES, } from './roles.js';
import { FEATURE_CATALOG, FEATURE_CATEGORIES, FEATURE_MODULES, FEATURE_PERM_PREFIX, } from './feature-catalog.js';
/** The Listings role (migration 20261002165427). Outside the `any` tier on purpose. */
export const BROKERAGE_ASSISTANT_ROLE_NAME = BROKERAGE_ASSISTANT_ROLE;
/**
 * Returns true if the role with the given name should, by default,
 * have access to a feature at the given tier. Tier membership is the
 * role sets in ./roles.ts, the ones the API's gates use.
 *
 * Used by the API: its boot sync grants a feature's default roles once,
 * when it creates the feature's `permissions` row
 * (services/feature-permissions/sync-feature-permissions-at-boot.ts), and
 * the Feature Access tab's "Apply default access" button re-applies them
 * on request (apply-feature-defaults.ts). Nothing else re-applies them, so
 * an admin's toggles stick.
 */
export function roleHasDefaultAccessToTier(roleName, tier) {
    if (tier === 'all')
        return true;
    if (tier === 'any')
        return roleName !== BROKERAGE_ASSISTANT_ROLE_NAME;
    if (tier === 'manager+')
        return MANAGER_PLUS_ROLES.has(roleName);
    if (tier === 'admin+')
        return ADMIN_ROLES.has(roleName);
    if (tier === 'research')
        return RESEARCH_TIER_ROLES.has(roleName);
    if (tier === 'manager+research')
        return MANAGER_PLUS_ROLES.has(roleName) || RESEARCH_TIER_ROLES.has(roleName);
    if (tier === 'listings') {
        return (MANAGER_PLUS_ROLES.has(roleName) ||
            roleName === BROKERAGE_ASSISTANT_ROLE_NAME ||
            roleName === BROKER_ROLE);
    }
    if (tier === 'listings_work') {
        return MANAGER_PLUS_ROLES.has(roleName) || roleName === BROKERAGE_ASSISTANT_ROLE_NAME;
    }
    return false;
}
/** Convert a feature key to the `perm_name` stored in the permissions table. */
export function featureKeyToPermName(key) {
    return `${FEATURE_PERM_PREFIX}${key}`;
}
/** Inverse of {@link featureKeyToPermName}; returns null if not a feature perm. */
export function permNameToFeatureKey(permName) {
    return permName.startsWith(FEATURE_PERM_PREFIX)
        ? permName.slice(FEATURE_PERM_PREFIX.length)
        : null;
}
/** True for any permission row that represents a feature gate. */
export function isFeaturePermission(permName) {
    return permName.startsWith(FEATURE_PERM_PREFIX);
}
/** Group the catalog by category, preserving category insertion order. */
export function groupFeaturesByCategory() {
    const order = Object.keys(FEATURE_CATEGORIES);
    return order.map((category) => ({
        category,
        label: FEATURE_CATEGORIES[category],
        features: FEATURE_CATALOG.filter((f) => f.category === category),
    }));
}
/**
 * Group the catalog by module, in {@link FEATURE_MODULES} order: each module's view switch first,
 * then its actions in catalog order.
 */
export function groupFeaturesByModule() {
    const order = Object.keys(FEATURE_MODULES);
    return order.map((module) => {
        const inModule = FEATURE_CATALOG.filter((f) => f.module === module);
        return {
            module,
            label: FEATURE_MODULES[module],
            features: [...inModule.filter((f) => f.kind === 'view'), ...inModule.filter((f) => f.kind === 'action')],
        };
    });
}
//# sourceMappingURL=feature-catalog-helpers.js.map